import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, UserCheck, Clock } from "lucide-react";
import {
  SimpleModal,
  SimpleModalHeader,
  SimpleModalTitle,
  SimpleModalDescription,
} from "../../../components/ui/simple-modal";
import { UserAvatar } from "../../../components/ui/avatar";
import { UserIdentity } from "../../../components/UserIdentity";
import { useAuth } from "../../../app/useAuth";
import { useFriendStatus } from "../../../hooks/useFriendStatus";
import { useFriends } from "../../../hooks/useFriends";
import { normalizeEmail } from "../../../lib/utils";
import { getUserLabel } from "../../../lib/identity";

/**
 * Modal that displays a list of voters/participants with friend actions.
 *
 * Props:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void
 * - users: array of { email, displayName, avatar, ... }
 * - anonymous: boolean - if true, show anonymized entries with no actions
 * - onViewProfile: (user) => void - called when "View Profile" is clicked
 */
export function VoterModal({ open, onOpenChange, users = [], anonymous = false, onViewProfile }) {
  const { user: currentUser } = useAuth();
  const { getStatus } = useFriendStatus();
  const { sendFriendRequest } = useFriends();
  const [sendingTo, setSendingTo] = useState(null);

  const currentEmail = normalizeEmail(currentUser?.email);

  const handleSendRequest = async (targetUser) => {
    const email = normalizeEmail(targetUser.email);
    if (!email) return;
    setSendingTo(email);
    try {
      await sendFriendRequest(email);
      toast.success("Friend request sent");
    } catch (err) {
      toast.error(err.message || "Failed to send friend request");
    } finally {
      setSendingTo(null);
    }
  };

  return (
    <SimpleModal open={open} onOpenChange={onOpenChange}>
      <div className="max-w-md">
        <SimpleModalHeader>
          <SimpleModalTitle>
            {anonymous ? "Anonymous Voters" : "Participants"}
          </SimpleModalTitle>
          <SimpleModalDescription>
            {anonymous
              ? "This poll uses anonymous voting. Identities are hidden."
              : `${users.length} participant${users.length !== 1 ? "s" : ""}`}
          </SimpleModalDescription>
        </SimpleModalHeader>

        <div className="mt-4 max-h-80 space-y-2 overflow-y-auto">
          {users.map((u, index) => {
            const email = normalizeEmail(u.email);
            const isSelf = email === currentEmail;

            // Modal-level coarse flag: every entry renders as a positional
            // "Anonymous voter N" with no actions (legacy path).
            if (anonymous) {
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-800"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-600">
                    <span className="text-sm font-semibold text-slate-500 dark:text-slate-300">
                      ?
                    </span>
                  </div>
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Anonymous voter {index + 1}
                  </span>
                </div>
              );
            }

            // Per-user eligibility (D5): an anonymized user object carries
            // isAnonymous:true from the foundation scrub. It must never reach
            // the named/friend-action branch (which would target the synthetic
            // `anon:` email). Render its alias with NO actions and NO real
            // identity. Self is never anonymized (anonymizeUsers self-exempts),
            // so self always falls through to the actionable named branch.
            if (u.isAnonymous === true) {
              return (
                <div
                  key={email || index}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-800"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-600">
                    <span className="text-sm font-semibold text-slate-500 dark:text-slate-300">
                      ?
                    </span>
                  </div>
                  <span className="min-w-0 flex-1 text-sm font-semibold text-slate-500 truncate dark:text-slate-400">
                    {u.displayName || "Anonymous voter"}
                  </span>
                </div>
              );
            }

            const status = isSelf ? "self" : getStatus(email);
            // Use the chosen public identifier for the aria-label — never the
            // raw email (the old `displayName || email` fallback leaked it when
            // displayName was empty).
            const actionLabel = getUserLabel(u) || "this participant";

            return (
              <div
                key={email || index}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 dark:border-slate-700 dark:bg-slate-800"
              >
                <button
                  type="button"
                  className={`flex min-w-0 flex-1 items-center gap-3 text-left ${
                    status === "friend" || status === "none"
                      ? "cursor-pointer rounded-lg transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50 -ml-1 pl-1 -my-1 py-1"
                      : "cursor-default"
                  }`}
                  disabled={isSelf || (status !== "friend" && status !== "none")}
                  onClick={() => {
                    if (status === "friend") {
                      onViewProfile?.(u);
                    } else if (status === "none") {
                      handleSendRequest(u);
                    }
                  }}
                  aria-label={
                    status === "friend"
                      ? `View profile of ${actionLabel}`
                      : status === "none"
                        ? `Send friend request to ${actionLabel}`
                        : undefined
                  }
                >
                  <div className="flex-shrink-0">
                    <UserAvatar user={u} email={u.email} src={u.avatar} size={36} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">
                      <UserIdentity user={u} />
                    </p>
                  </div>
                </button>
                <div className="flex-shrink-0">
                  {isSelf ? (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                      You
                    </span>
                  ) : status === "friend" ? (
                    <button
                      type="button"
                      onClick={() => onViewProfile?.(u)}
                      className="flex items-center gap-1 rounded-full bg-brand-primary/10 px-3 py-1 text-xs font-semibold text-brand-primary transition-colors hover:bg-brand-primary/20"
                    >
                      <UserCheck className="h-3 w-3" />
                      View Profile
                    </button>
                  ) : status === "pending_outgoing" ? (
                    <span className="flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-400 dark:bg-slate-700 dark:text-slate-500">
                      <Clock className="h-3 w-3" />
                      Request Sent
                    </span>
                  ) : status === "pending_incoming" ? (
                    <span className="flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
                      <Clock className="h-3 w-3" />
                      Pending
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSendRequest(u)}
                      disabled={sendingTo === email}
                      className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                    >
                      <UserPlus className="h-3 w-3" />
                      {sendingTo === email ? "Sending..." : "Friend Request"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SimpleModal>
  );
}
